import React, { useEffect } from 'react'
import { Field } from 'formik'
import { Col, Row, Form as BForm, Card, Badge } from '@themesberg/react-bootstrap'
import cloneDeep from 'lodash/cloneDeep'
import { toast } from '../Toast'
import { filterAdminSubModules, updateUserPermissions } from '../../utils/permissions'
import { Button } from 'react-bootstrap'
const SubModulePermission = ({ permissions, setFieldValue, viewOnly = false, values, permissionKey = 'permission', fetchGroupDetail }) => {
  useEffect(() => {
    const updatedUserPermissions = updateUserPermissions(values.subModulePermissions, values[permissionKey])
    setFieldValue(permissionKey, updatedUserPermissions)
  }, [values.subModulePermissions])
  const toggleSubModuleEnabled = (key, subKey, subModulePermissions) => {
    const updatedPermissions = cloneDeep(subModulePermissions)

    if (subKey) {
      // Handle submodule toggle
      if (updatedPermissions[key]) {
        const subModules = updatedPermissions[key].subModules

        const subModuleIndex = subModules.findIndex(
          (subModule) => subModule.key === subKey
        )
        if (subModuleIndex !== -1) {
          subModules[subModuleIndex] = {
            ...subModules[subModuleIndex],
            enabled: !subModules[subModuleIndex].enabled
          }
        }
      }
    } else {
      // Handle main module toggle
      if (updatedPermissions[key]) {
        const moduleEnabled = !updatedPermissions[key].enabled
        updatedPermissions[key].enabled = moduleEnabled

        // Update all submodules' enabled status
        updatedPermissions[key].subModules = updatedPermissions[key].subModules.map(
          (subModule) => ({
            ...subModule,
            enabled: moduleEnabled
          })
        )
      }
    }

    // Ensure `moduleOrder` is preserved and return sorted updated permissions
    return Object.fromEntries(
      Object.entries(updatedPermissions).map(([k, value]) => [
        k,
        {
          ...value,
          moduleOrder: value.moduleOrder, // Explicitly maintain moduleOrder
          subModules: value.subModules.map((sub) => ({
            ...sub,
            subModuleOrder: sub.subModuleOrder // Explicitly maintain subModuleOrder
          }))
        }
      ])
    )
  }

  const handleToggle = (key, subKey) => {
    if (viewOnly) {
      toast('Permissions cannot be edited in view-only mode.', 'info')
      return
    }
    const isParentModule = !subKey

    if (!isParentModule) {
      // Check if the parent module is enabled before toggling a submodule
      const parentModule = permissions[key]
      if (!parentModule.enabled) {
        toast(
          'Please enable permission before selecting other for this module',
          'error'
        )
        return
      }
    }

    const updatedPermissions = toggleSubModuleEnabled(key, subKey, permissions)
    setFieldValue('subModulePermissions', updatedPermissions)
    const updatedUserPermissions = updateUserPermissions(updatedPermissions, values[permissionKey])
    setFieldValue(permissionKey, updatedUserPermissions)
  }

  const renderBadge = React.useCallback(
    (key, name, enabled, subKey) => (
      <Badge
        className='p-2 mx-2'
        type='button'
        bg={enabled ? 'success' : 'primary'}
        onClick={() => handleToggle(key, subKey)}
      >
        {name}
      </Badge>
    ),
    [handleToggle]
  )

  const renderPermissionField = React.useCallback(
    (key, name, enabled, subKey) => (
      <label key={`${key}-${subKey || name}`}>
        <Field
          className='d-none'
          type='checkbox'
          name={`subModulePermissions.${key}${subKey ? `.subModules[${subKey}]` : ''}`}
          value={name}
          checked={enabled}
          onChange={() => {}}
        />

        <h3>{renderBadge(key, name, enabled, subKey)}</h3>
      </label>
    ),
    [renderBadge]
  )

  const sortPermissions = (unsortedPermissions) => {
    if (!unsortedPermissions || typeof unsortedPermissions !== 'object') {
      return {}
    }
    return Object.fromEntries(
      Object.entries(unsortedPermissions)
        .sort(([, a], [, b]) => a.moduleOrder - b.moduleOrder)
    )
  }

  const sortedPermissions = React.useMemo(() => sortPermissions(permissions), [permissions])
  const updatedModulesPermission = filterAdminSubModules(sortedPermissions, values)
  return (
    <Card className='mt-3'>
      <Card.Header>
        <div className='d-flex'>
          <span className='col-4 header-font-weight'>Module/SubModule</span>
          <span className='ms-3 header-font-weight'>Permissions</span>&nbsp;
          {values?.groupId &&
            <Button
              size='sm'
              variant='outline-success'
              onClick={fetchGroupDetail}
            >
              Fetch permission
            </Button>}
        </div>
      </Card.Header>
      {permissions && (
        <Card.Body>
          {Object.entries(updatedModulesPermission).map(([key, { name, enabled, subModules }]) => (
            (subModules && subModules.length > 0) && ( // Render only if subModules exist
              <Row className='border-bottom mt-3' key={key}>
                <Col lg={4}>
                  <BForm.Label>{name}</BForm.Label>
                </Col>
                <Col className='d-flex flex-wrap'>
                  {renderPermissionField(key, 'Enabled', enabled)}
                  {subModules.map(({ key: subKey, name: subName, enabled: subEnabled }) =>
                    renderPermissionField(key, subName, subEnabled, subKey)
                  )}
                </Col>
              </Row>
            )
          ))}
        </Card.Body>
      )}
    </Card>
  )
}

export default SubModulePermission
